import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip,Drawer, Grid} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchModelsList, deleteModel } from "../modelsThunks";
import { App } from "antd";
import { selectModelsList } from "../modelsSelectors";
import { setSelectedModel, setIsEditModel } from "../modelsSlice";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const ModelsList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdminUser = useIsAdminUser();
  const modelsList = useSelector(selectModelsList);
  let [access_type, access_type_set] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    const access = getAccessType("model_manage");
    access_type_set(access);
  }, []);

  const handleDelete = async (model_id, model_name) => {
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete model: ${model_name}`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteModel(model_id)).unwrap();
          modal.success({ title: "Model deleted successfully" });
          dispatch(fetchModelsList());
        } catch (err) {
          modal.error({ title: err.message || "Delete failed" });
        }
      },
    });
  };

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "Model Name", dataIndex: "model_name" },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                disabled={!(isAdminUser || canEdit)}
                onClick={() => {
                  dispatch(setSelectedModel(record));
                  dispatch(setIsEditModel(true));
                  navigate("addNewModel");
                }}
                icon={<EditOutlined />}
              />
              <Button
                type="link"
                disabled={!(isAdminUser || canDelete)}
                danger
                onClick={() =>
                  handleDelete(record.model_id, record.model_name)
                }
                icon={<DeleteOutlined />}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    dispatch(setSelectedModel(null));
    dispatch(setIsEditModel(false));
    dispatch(fetchModelsList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedModel(null));
                  dispatch(setIsEditModel(false));
                  navigate("/inventory/models-manage/addNewModel");
                }}
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={modelsList}
        rowKey="model_id"
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Model Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Item Model Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Item Model module is used to create and manage product models
            within the ERP system. Models help classify inventory items for
            easier identification, organization, and reporting.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View existing item models</li>
              <li>Use <strong>Add New</strong> to create models</li>
              <li>Edit model details when required</li>
              <li>Delete unused models if permitted</li>
              <li>Organize inventory items using models</li>
              <li>Manage item classification for reporting purposes</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Model names should remain unique.</li>
              <li>Deleting models may affect linked inventory items.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Models improve inventory organization and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ModelsList;
